import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable, Pipe, PipeTransform } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, combineLatest, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DisplayDetailsModalComponent } from '../core/modals/display-details/display-details.component';
import { ErrorModalComponent } from '../core/modals/error-modal/error-modal.component';
import { AuthenticationService } from '../core/services/authentication.service';
import { LogCategory, LoggerService, LogService, LogType } from '../core/services/logger.service';
import { ProfileService } from '../core/services/profile.service';
import { UtilityService } from '../core/services/utility.service';

const CACHE_SIZE = 1;

@Injectable()
export class EnrollmentsService {
    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
        })
    };
    
    private _apiUrl = environment.apiUrl + '/enrollments';
    private _apiUrlRounds = environment.apiUrl + '/enrollments/rounds';
    private _apiUrlHistory = environment.apiUrl + '/enrollments/history';    
    private _enrollmentRounds$: Observable<any>;    
    private _subNewUser: Subscription = null;            // new user subscription for spoof/unspoof events
    public sortSectionsByTitle = true;
    public enrollmentCount: number = 0;
    public conflictCount: number = 0;
    private _personId;
    public isDropRunning: any[] = [];
    
    // Returns an observable for the current enrollments, this can then be refreshed for add/drop
    private _enrollmentsSubject: BehaviorSubject<any> = new BehaviorSubject(null);
    public get enrollments$() {        
        return this._enrollmentsSubject as Observable<any>;
    }

    private _enrollmentHistorySubject: BehaviorSubject<any> = new BehaviorSubject(null);
    public get enrollmentHistory$() {
        return this._enrollmentHistorySubject as Observable<any>;
    }

    constructor (
        private http: HttpClient, 
        private auth: AuthenticationService,
        private logger: LoggerService,
        private utility: UtilityService,
        private modalService: NgbModal,
        private profileService: ProfileService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
        this._subNewUser = auth.loggedInUser$.subscribe((data) => {
            // Clear the enrollments observable and cause it to be refetched             
            this._enrollmentRounds$ = null;
            this._personId = data;
            // just access the property to cause refetch. The variable probably isn't necessary, but it's clearer this way.             
            const temp2 = this.enrollmentRounds$;
            this.fetchEnrollments();
            this.fetchEnrollmentHistory();
        })
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.Enrollments, category);
    }

    private fetchEnrollments() {
        this.log('Fetch Enrollments');
        this.http.get<any>(this._apiUrl, this.httpOptions).subscribe((data) => {
            this.log('fetch enrollments', 'Enrollments recieved', LogCategory.SubscriptionData);
            if (data != null) {
                this._enrollmentsSubject.next(data);
                this.enrollmentCount = data.enrollments.length;
                this.conflictCount = data.enrollments.filter(section => section.Status == 'Waitlisted' && section.Conflicts.length > 0).length;
            }
        });
    }

    public updateConflicts$: BehaviorSubject<any> = new BehaviorSubject(null); //updated when a conflict is managed
    public removeConflicts$: BehaviorSubject<number> = new BehaviorSubject(0); //updated when a class is dropped
    //  managedConflicts - The list of conflicts currently being managed
    //  Watches:  User enrollments, updateConflicts, removeConflicts
    //  Emits:    List of managed conflicts
    //  Function: Emit the list of managed conflicts based on current enrollments
    public managedConflicts$: Observable<any[]> = combineLatest(this._enrollmentsSubject, this.updateConflicts$, this.removeConflicts$)
        .pipe(
            map(([data, updatedConflict, removeConflict]) => {
                var managedConflicts = [];
                data.enrollments.forEach(e => {
                    if ((e.Status == 'Waitlisted') && (e.Conflicts.length > 0)) {
                        e.Conflicts.forEach(section => {
                            section.Conflicts.forEach(conflict => {
                                if (conflict.IsDroppable)
                                    managedConflicts.push({ section: section.SectionId, conflict: conflict.SectionId })
                            })
                        })
                    }
                });
                //add or remove based on waitlist managed checkbox
                if (!!updatedConflict) {
                    if (!updatedConflict.isDroppable)
                        managedConflicts.push({ section: updatedConflict.section, conflict: updatedConflict.conflict });
                    else
                        managedConflicts = managedConflicts.filter(con => {
                            return !((con.section == updatedConflict.section) && (con.conflict == updatedConflict.conflict));
                        });
                }
                //remove because the class has been dropped
                if (removeConflict > 0)
                    managedConflicts = managedConflicts.filter(con => con.section != removeConflict);
         
               return managedConflicts;
            }),          
    );
    
    
    public updateDismissed$: BehaviorSubject<number[]> = new BehaviorSubject([]); //updated when an alert is dismissed
    //  currentRounds - The list of rounds the user enrollments are in
    //  Watches:  User enrollments, updateDismissed
    //  Emits:    List of round ids
    //  Function: Emit the list of rounds ids for the current enrollments, exclude any round ids
    //            for which an alert has been dismissed
    public currentRounds$: Observable<number[]> = combineLatest(this._enrollmentsSubject, this.updateDismissed$)
        .pipe(
            map(([data, dismissed]) => {
                var dismissedAlerts = [];
                if (this.profileService.componentStore.Catalog.Data.DismissedWaitlistAlert)
                    dismissedAlerts = this.profileService.componentStore.Catalog.Data?.DismissedWaitlistAlert
                        .split(",")
                        .map(str => { return Number(str) });

                var currentRounds = [];
                var enrollmentsInARound = data.enrollments.filter(enrollment => enrollment.Section.RoundId.length);
                enrollmentsInARound.forEach(enrollment => {
                    enrollment.Section.RoundId.forEach(roundId => {                       
                        if ((!currentRounds.includes(roundId)) && (!dismissedAlerts.includes(roundId)) && (!dismissed.includes(roundId)) )
                           currentRounds.push(roundId);
                    })
                })
                return currentRounds;
            })
        );



    public fetchEnrollmentHistory() {
        this.log('Fetch Enrollment History');
        this.http.get<any>(this._apiUrlHistory, this.httpOptions).subscribe((data) => {
            this.log('fetch enrollments', 'Enrollment history recieved', LogCategory.SubscriptionData);
            this._enrollmentHistorySubject.next(data)
        });
    }

    public get enrollmentRounds$(): Observable<any> {
        if (this._enrollmentRounds$) {
            //this.log("enrollmentRounds$", 'returned cached enrollmentRounds$')
        } else {
            this._enrollmentRounds$ = this.http.get<any>(this._apiUrlRounds, this.httpOptions)
                .pipe(
                    tap(_ => this.log('getEnrollmentRounds$', 'fetched enrollment rounds via api', LogCategory.SubscriptionData)),
                    catchError(this.handleError('getEnrollmentRounds', [])),
                    shareReplay(CACHE_SIZE)
                );
        }
        return this._enrollmentRounds$;
    }

    private _enrollmentActive$: Observable<any> = null;
    public get enrollmentActive$(): Observable<any> {
        if (this._enrollmentActive$) {            
        } else {
            
            this._enrollmentActive$ = combineLatest(timer(0, 3000), this.enrollmentRounds$).pipe(
                map(([a, rounds]) => {                    
                    var activeRounds = [];
                    const now = this.utility.getCurrentDate();
                    //console.log("-- " + now + " --");

                    rounds.forEach((item) => {
                        const ads = new Date(item.AddStarts);
                        const ade = new Date(item.AddEnds);
                        const drs = new Date(item.DropStarts);
                        const dre = new Date(item.DropEnds);



                        var adding = (ads <= now) && (ade > now);
                        var dropping = (drs <= now) && (dre > now);

                        var active = adding || dropping;

                        // Look for start dates within the next 14 days
                        var upcoming = ((ads > now) && Math.round((ads.getDate() - now.getDate()) / (1000 * 60 * 60 * 24)) < 14)
                            || ((drs > now) && Math.round((drs.getDate() - now.getDate()) / (1000 * 60 * 60 * 24)) < 14);

                        var upcomingDate = null;
                        if (ads > now)
                            upcomingDate = ads;
                        else if (drs > now)
                            upcomingDate = drs;

                        //console.log("ads-" + ads + " - ade-" + ade + " - drs-" + drs + " - dre-" + dre + " - now-" + now + " - upcomingDate-" + upcomingDate + " - adding-" + adding + " - dropping-" + dropping + " - active-" + active + " - upcoming-" + upcoming);




                        activeRounds[item.Id] = ({ adding, dropping, active, upcoming, upcomingDate });


                    })                    
                    return activeRounds;
                    
                }),
                shareReplay(CACHE_SIZE)
            )
        }        
        return this._enrollmentActive$;
    }

    private _roundsActive$: Observable<any> = null;
    public get roundsActive$(): Observable<any> {
        if (this._roundsActive$) {
        } else {
            this._roundsActive$ = this.enrollmentActive$.pipe(                
                map((data) => {
                    var hasActiveRound = data.some(i => i.active);
                    var hasUpcoming = data.some(i => i.upcoming);
                    //console.log("data", data);
                    if (data.length > 0)
                        var upcomingDate = data.reduce((a, b) => a.upcomingDate < b.upcomingDate ? a : b).upcomingDate;
                    var activeCount = data.filter(i => i.active).length;
                    return { hasActiveRound, activeCount, hasUpcoming, upcomingDate };
                }),
                shareReplay(CACHE_SIZE)
            )
        }
        return this._roundsActive$;
    }


    
    public add(section: any) {

        const enrollment = {
            SectionId: section.SectionId,
            PersonId: parseInt(this._personId),
            SeatAllocation: section.SeatAllocation
        };

        return this.http.post<any>(this._apiUrl, enrollment, this.httpOptions).pipe(
                tap(() => this.fetchEnrollments() ),
                tap(() => this.fetchEnrollmentHistory() )
        )
    }

    public drop(SectionId) {
            
        var httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: {
                SectionId,
                PersonId: parseInt(this._personId)
            }
        }

       return this.http.delete<any>(this._apiUrl, httpOptions).pipe(
           tap(() => this.fetchEnrollments()), //refetch enrollments
           tap(() => this.fetchEnrollmentHistory())
        )
    }

    public get hasActiveDropAdd$(): Observable<any> {
        if (!this.auth.hasFeature('OnlineDropAdd')) {
            return of([{active: false }]).pipe(shareReplay(CACHE_SIZE));
        }        
        return this.enrollmentActive$;
    }

    private handleError<T> (operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {

            // TODO: send the error to remote logging infrastructure
            // console.error(error); // log to the console instead

            // Let the app keep running by returning an empty result.
            return of( result as T );
        };
    }

    public updateWaitlist(sectionId, conflictingSectionId, isDroppable) {

        const conflict = {
            PersonId: this._personId,
            SectionId: sectionId,
            ConflictingSectionId: conflictingSectionId
        };

        var deleteHttpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: conflict
        }
        this.updateConflicts$.next({
            section: sectionId, conflict: conflictingSectionId, isDroppable: isDroppable
        });

        if (!isDroppable) {

            return this.http.post<any>(this._apiUrl + '/conflictEntry', conflict, this.httpOptions).pipe(
                tap(() => this.fetchEnrollmentHistory())
            )
        }   
        else {
            return this.http.delete<any>(this._apiUrl + '/conflictEntry', deleteHttpOptions).pipe(
                tap(() => this.fetchEnrollmentHistory())
            )
        }
    }

    //  Confirmation window before Drop/Abandon is submitted
    public confirmDrop(section: any, buttonType: string, isDropped: EventEmitter<boolean> = null) {
        this.isDropRunning[section.Id] = true;
        const modalRefConfirm = this.openAbandonDropConfirmModal(section, buttonType);

        //  Modal is submitted
        modalRefConfirm.result.then((result) => {
            //  Finite observable, no need to unsubscribe
            this.drop(section.Id).pipe().subscribe((result) => {
                if (result.IsSuccess) {
                    section.AllowsAdd = result.AllowsAdd;
                    section.AllowsDrop = false;
                    section.IsWaitlisted = false;

                    //  If we're managing clonflicts update the filter
                    this.removeConflicts$.next(section.Id);

                    //  EventEmitter is emitted back to close the modal for the section in My Courses
                    if(!!isDropped)
                        isDropped.emit(true);
                }
                this.openEnrollmentResultModal(result);
            },
                (result) => {
                    this.modalService.open(ErrorModalComponent);
                });
        },
            //  Modal is dismissed, no action needed
            () => { })
            //  Always stop the spinner once drop attempt is complete
            .finally(() => this.isDropRunning[section.Id] = false );
    }

    public openEnrollmentResultModal(result) {
        const modalRef = this.modalService.open(DisplayDetailsModalComponent);
        if (result['IsSuccess']) 
            modalRef.componentInstance.title = '<span class="ms-1">' +
                (result['ActionTaken'] == 'Waitlist' ? '' : 'Success - ') +
                result['ActionRequested'] + '</span>';
        else
            modalRef.componentInstance.title = `<i class="fas fa-exclamation-circle text-red"></i><span class="ms-1"> Unable to ${result['ActionRequested']}</span>`;

        modalRef.componentInstance.body = result['UserMessage'];
        modalRef.componentInstance.okButton = "Got it!";
    }

    public openAbandonDropConfirmModal(section, buttonType) {
        const modalRefConfirm = this.modalService.open(DisplayDetailsModalComponent);
        modalRefConfirm.componentInstance.title = `<i class="fas fa-exclamation-circle text-red"></i> Confirm ${buttonType} Class`;
        modalRefConfirm.componentInstance.body = `<p><strong>Are you sure you want to ${buttonType} the following class? </strong></p>
        <ul>`;
        
        if (section.CourseCode)
            modalRefConfirm.componentInstance.body += `<li><strong>Course:</strong> ${section.CourseCode} </li>`;
        if (section.DisplayNameStudents)
            modalRefConfirm.componentInstance.body += `<li><strong>Course:</strong> ${section.DisplayNameStudents}</li>`;
        if (section.SectionNumber)
            modalRefConfirm.componentInstance.body += `<li><strong>Section:</strong> ${section.SectionNumber}</li>`;
        if (section.Title)
            modalRefConfirm.componentInstance.body += `<li><strong>Title:</strong> ${section.Title}</li>`;

        modalRefConfirm.componentInstance.body += `</ul>
        <p><strong>This operation can not be undone.</strong></p>`;
        modalRefConfirm.componentInstance.okButton = `${buttonType} class`;
        modalRefConfirm.componentInstance.useTwoButtons = true;
        modalRefConfirm.componentInstance.useDangerColor = true;

        return modalRefConfirm;
    }

}
